// ========================================================================
// 🏥 SERVICIO DE REGISTROS MÉDICOS
// ========================================================================

import {
  API_BASE_MEDICAL_RECORDS_URL,
  API_MEDICAL_RECORDS_LIST,
  API_MEDICAL_RECORDS_DETAILS,
  API_MEDICAL_RECORDS_CREATE,
  API_MEDICAL_RECORDS_UPDATE,
  API_MEDICAL_RECORDS_DELETE,
  API_MEDICAL_RECORDS_UPDATE_STATUS,
  API_MEDICAL_RECORDS_PATIENT_STATS
} from '../../../helpers/urlHelper';

class MedicalRecordsService {
  constructor() {
    this.baseURL = API_BASE_MEDICAL_RECORDS_URL;
  }

  // ========================================================================
  // 📋 OBTENER TODOS LOS REGISTROS MÉDICOS CON FILTROS Y PAGINACIÓN
  // ========================================================================
  async getAllRecords(queryParams = {}) {
    try {
      const params = new URLSearchParams();
      
      // Parámetros de paginación
      if (queryParams.page) params.append('page', queryParams.page);
      if (queryParams.limit) params.append('limit', queryParams.limit);
      
      // Parámetros de ordenamiento
      if (queryParams.sortBy) params.append('sortBy', queryParams.sortBy);
      if (queryParams.sortOrder) params.append('sortOrder', queryParams.sortOrder);
      
      // Filtros
      if (queryParams.patientId) params.append('patientId', queryParams.patientId);
      if (queryParams.doctorId) params.append('doctorId', queryParams.doctorId);
      if (queryParams.status) params.append('status', queryParams.status);
      if (queryParams.category) params.append('category', queryParams.category);
      if (queryParams.priority) params.append('priority', queryParams.priority);
      if (queryParams.fromDate) params.append('fromDate', queryParams.fromDate);
      if (queryParams.toDate) params.append('toDate', queryParams.toDate);
      if (queryParams.search) params.append('search', queryParams.search);

      const queryString = params.toString();
      const url = `${this.baseURL}${API_MEDICAL_RECORDS_LIST}${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // Agregar headers de autenticación si es necesario
          // 'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error(`Error al obtener registros médicos: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getAllRecords:', error);
      throw error;
    }
  }

  // ========================================================================
  // 📄 OBTENER UN REGISTRO MÉDICO POR ID
  // ========================================================================
  async getRecordById(recordId) {
    try {
      const response = await fetch(`${this.baseURL}${API_MEDICAL_RECORDS_DETAILS}/${recordId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error(`Error al obtener registro médico: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getRecordById:', error);
      throw error;
    }
  }

  // ========================================================================
  // ✅ CREAR NUEVO REGISTRO MÉDICO
  // ========================================================================
  async createRecord(recordData) {
    try {
      const response = await fetch(`${this.baseURL}${API_MEDICAL_RECORDS_CREATE}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(recordData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error al crear registro médico: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en createRecord:', error);
      throw error;
    }
  }

  // ========================================================================
  // 🔄 ACTUALIZAR REGISTRO MÉDICO COMPLETO
  // ========================================================================
  async updateRecord(recordId, recordData) {
    try {
      const response = await fetch(`${this.baseURL}${API_MEDICAL_RECORDS_UPDATE}/${recordId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(recordData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error al actualizar registro médico: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en updateRecord:', error);
      throw error;
    }
  }

  // ========================================================================
  // 🔄 ACTUALIZAR SOLO EL ESTADO DEL REGISTRO MÉDICO
  // ========================================================================
  async updateRecordStatus(recordId, status) {
    try {
      const response = await fetch(`${this.baseURL}${API_MEDICAL_RECORDS_UPDATE_STATUS}/${recordId}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error al actualizar estado: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en updateRecordStatus:', error);
      throw error;
    }
  }

  // ========================================================================
  // 🗑️ ELIMINAR REGISTRO MÉDICO (SOFT DELETE)
  // ========================================================================
  async deleteRecord(recordId) {
    try {
      const response = await fetch(`${this.baseURL}${API_MEDICAL_RECORDS_DELETE}/${recordId}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `Error al eliminar registro médico: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en deleteRecord:', error);
      throw error;
    }
  }

  // ========================================================================
  // 📊 OBTENER ESTADÍSTICAS DE REGISTROS MÉDICOS DE UN PACIENTE
  // ========================================================================
  async getPatientStats(patientId) {
    try {
      const response = await fetch(`${this.baseURL}${API_MEDICAL_RECORDS_PATIENT_STATS}/${patientId}/stats`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          // 'Authorization': `Bearer ${token}`
        },
      });

      if (!response.ok) {
        throw new Error(`Error al obtener estadísticas del paciente: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error en getPatientStats:', error);
      throw error;
    }
  }

  // ========================================================================
  // 🔍 MÉTODOS AUXILIARES PARA FILTROS ESPECÍFICOS
  // ========================================================================

  // Obtener registros por paciente
  async getRecordsByPatient(patientId, queryParams = {}) {
    return this.getAllRecords({ ...queryParams, patientId });
  }

  // Obtener registros por doctor
  async getRecordsByDoctor(doctorId, queryParams = {}) {
    return this.getAllRecords({ ...queryParams, doctorId });
  }

  // Obtener registros por estado
  async getRecordsByStatus(status, queryParams = {}) {
    return this.getAllRecords({ ...queryParams, status });
  }

  // Obtener registros por categoría
  async getRecordsByCategory(category, queryParams = {}) {
    return this.getAllRecords({ ...queryParams, category });
  }

  // Obtener registros por prioridad
  async getRecordsByPriority(priority, queryParams = {}) {
    return this.getAllRecords({ ...queryParams, priority });
  }

  // Obtener registros en un rango de fechas
  async getRecordsByDateRange(fromDate, toDate, queryParams = {}) {
    return this.getAllRecords({ ...queryParams, fromDate, toDate });
  }

  // Buscar registros médicos
  async searchRecords(searchTerm, queryParams = {}) {
    return this.getAllRecords({ ...queryParams, search: searchTerm });
  }
}

// Exportar instancia única del servicio
export const medicalRecordsService = new MedicalRecordsService();
export default medicalRecordsService;